
import { supabase } from "@/integrations/supabase/client";
import { Chapter } from "../types";
import { toast } from "sonner";

export async function fetchChapter(webnovelId: string, chapterNumber: number): Promise<Chapter> {
  try {
    console.log(`Fetching chapter ${chapterNumber} for webnovel ${webnovelId}`);
    
    // Get the chapter by webnovel_id and chapter_number
    const { data, error } = await supabase
      .from("chapters")
      .select("*")
      .eq("webnovel_id", webnovelId)
      .eq("chapter_number", chapterNumber)
      .maybeSingle(); // Use maybeSingle instead of single to prevent errors
    
    if (error) {
      console.error("Error fetching chapter:", error);
      throw new Error(`Erro ao buscar capítulo: ${error.message}`);
    }
    
    if (!data) {
      console.log("Specific chapter not found, trying to get the first chapter");
      // If the specific chapter doesn't exist, try to fetch any chapter for this webnovel
      const { data: chaptersData, error: chaptersError } = await supabase
        .from("chapters")
        .select("*")
        .eq("webnovel_id", webnovelId)
        .order("chapter_number", { ascending: true })
        .limit(1);
        
      if (chaptersError) {
        console.error("Error fetching any chapter:", chaptersError);
        throw new Error(`Erro ao buscar qualquer capítulo: ${chaptersError.message}`);
      }
      
      if (!chaptersData || chaptersData.length === 0) {
        throw new Error(`Nenhum capítulo encontrado para esta webnovel. ID: ${webnovelId}`);
      }
      
      console.log("Found first available chapter:", chaptersData[0]);
      
      // Record view for this chapter
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id || null;
        
        // Use direct insert instead of RPC function
        if (chaptersData[0].id) {
          await supabase
            .from("chapter_views")
            .insert({
              chapter_id: chaptersData[0].id,
              user_id: userId
            });
        }
      } catch (viewError) {
        console.error("Error recording chapter view:", viewError);
        // Don't throw here, it's not critical
      }
      
      return chaptersData[0] as Chapter;
    }
    
    console.log("Chapter data:", data);
    
    // Record view for this chapter
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || null;
      
      // Use direct insert instead of RPC function
      await supabase
        .from("chapter_views")
        .insert({
          chapter_id: data.id,
          user_id: userId
        });
    } catch (viewError) {
      console.error("Error recording chapter view:", viewError);
      // Don't throw here, it's not critical
    }
    
    const chapter: Chapter = {
      id: data.id,
      webnovel_id: data.webnovel_id,
      chapter_number: data.chapter_number,
      title: data.title,
      content: data.content,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
    
    return chapter;
  } catch (error: any) {
    console.error("Error in fetchChapter:", error);
    toast.error(error.message || "Erro ao carregar capítulo");
    throw error;
  }
}

// New function to get chapter view count
export async function getChapterViewCount(chapterId: string): Promise<number> {
  try {
    // Use a direct count query instead of RPC
    const { count, error } = await supabase
      .from("chapter_views")
      .select("id", { count: 'exact', head: true })
      .eq("chapter_id", chapterId);
    
    if (error) {
      console.error("Error getting chapter view count:", error);
      return 0;
    }
    
    return count || 0;
  } catch (error) {
    console.error("Error in getChapterViewCount:", error);
    return 0;
  }
}
